<main>
  <header>
    <h1>Let's get started</h1>
    <h2>Enter owner or authorizing individual's information</h2>
  </header>
  <form class="application-form" [formGroup]="form" (ngSubmit)="continue()" appPopulateForm [formType]="'personalDetails'" [form]="form">
    <!-- Owner Checkbox -->
    <div class="field owner-checkbox">
      <mat-checkbox formControlName="isOwner" (change)="onOwnerCheckboxChange($event)">
        I am an owner of the business
      </mat-checkbox>
    </div>

    <div class="form-row">
      <!-- First Name -->
      <div class="field first-name">
        <label for="firstName">First Name</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="firstName" required placeholder="Enter">
          <mat-error *ngIf="form.get('firstName')?.hasError('required')">
            First Name is required
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Middle Initial -->
      <div class="field middle-initial">
        <label for="middleInitial">Middle Initial</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="middleInitial" placeholder="Enter" maxlength="1">
          <mat-error *ngIf="form.get('middleInitial')?.hasError('pattern')">
            Please enter a single letter
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Last Name -->
      <div class="field last-name">
        <label for="lastName">Last Name</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="lastName" required placeholder="Enter">
          <mat-error *ngIf="form.get('lastName')?.hasError('required')">
            Last Name is required
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- SSN -->
    <div class="field" *ngIf="role === 'borrower'">
      <label for="ssn">SSN</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <input matInput formControlName="ssn" mask="XXX-XX-XXXX" required [hiddenInput]="ssnMasked"
          [patterns]="ssnReplacePattern" placeholder="Enter">
        <button type="button" mat-icon-button class="ssn-eye" matSuffix (click)="toggleSSNVisibility($event)" matTooltip="Show/Hide SSN">
          <img src="icons/lock.svg" alt="Lock Icon" />
        </button>
        <mat-error *ngIf="form.get('ssn')?.hasError('required')">
          SSN is required
        </mat-error>
        <mat-error *ngIf="form.get('ssn')?.hasError('mask')">
          SSN must be in the format XXX-XX-XXXX
        </mat-error>
      </mat-form-field>
      <p class="field-info">Your SSN helps us verify your details and prevent fraud. We'll keep your data secure.</p>
    </div>

    <!-- Date of Birth -->
    <div class="field">
      <label for="dateOfBirth">Date of Birth</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <input
          matInput
          formControlName="dateOfBirth"
          [max]="getMinDate()"
          placeholder="__/__/____"
          mask="M0/d0/0000"
          [dropSpecialCharacters]="false"
          appDateFormat>

        <mat-error *ngIf="form.get('dateOfBirth')?.hasError('required')">
          Date of Birth is required
        </mat-error>
        <mat-error *ngIf="form.get('dateOfBirth')?.hasError('tooYoung')">
          You must be at least 18 years old
        </mat-error>
        <mat-error *ngIf="form.get('dateOfBirth')?.hasError('invalid') || form.get('dateOfBirth')?.hasError('mask')">
          Date of Birth is invalid
        </mat-error>
      </mat-form-field>
    </div>

    <!-- Street Address -->
    <div class="field">
      <label for="streetAddress">Street Address</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <input matInput formControlName="streetAddress" required placeholder="Enter">
        <mat-error *ngIf="form.get('streetAddress')?.hasError('required')">
          Street Address is required
        </mat-error>
      </mat-form-field>
    </div>

    <div class="form-row">
      <!-- Suite/Apt/Other -->
      <div class="field">
        <label for="suiteUnitOffice">Suite/Apt/Other (optional)</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="suiteUnitOffice" placeholder="Enter">
        </mat-form-field>
      </div>

      <!-- City -->
      <div class="field">
        <label for="city">City</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="city" required placeholder="Enter">
          <mat-error *ngIf="form.get('city')?.hasError('required')">
            City is required
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <div class="form-row">
      <!-- State -->
      <div class="field">
        <label for="state">State</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <mat-select formControlName="state" placeholder="Enter">
            <mat-option [value]="state" *ngFor="let state of usStates">{{state}}</mat-option>
          </mat-select>
          <mat-error *ngIf="form.get('state')?.hasError('required')">
            Please enter a valid state abbreviation
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Zip Code -->
      <div class="field">
        <label for="zipCode">Zip Code</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="zipCode" required type="text" maxlength="5" pattern="\d{5}" inputmode="numeric"
            (keypress)="onlyNumberKey($event)" placeholder="Enter">
          <mat-error *ngIf="form.get('zipCode')?.hasError('required')">
            Zip Code is required
          </mat-error>
          <mat-error *ngIf="form.get('zipCode')?.hasError('pattern')">
            Zip Code must be exactly 5 digits
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <div class="form-row">
      <!-- Email Address -->
      <div class="field">
        <label for="emailAddress">Your Company Email Address</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="emailAddress" required placeholder="Enter">
          <mat-error *ngIf="form.get('emailAddress')?.hasError('required')">
            Email Address is required
          </mat-error>
          <mat-error *ngIf="form.get('emailAddress')?.hasError('email')">
            Please enter a valid email address
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Mobile Number -->
      <div class="field">
        <label for="phoneNumber">Mobile Number</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="phoneNumber" required type="tel" mask="(000) 000-0000" maxlength="15"
            inputmode="tel" (keypress)="onlyNumberKey($event)" placeholder="Enter">
          <mat-error *ngIf="form.get('phoneNumber')?.hasError('required')">
            Mobile Number is required
          </mat-error>
          <mat-error *ngIf="form.get('phoneNumber')?.hasError('mask')">
            Mobile Number must be 10 digits
          </mat-error>
        </mat-form-field>
        <p class="field-info">By giving us your mobile number, you authorize
          Transend to contact you at the number provided using automated technology
          for servicing your accounts.
        </p>
      </div>
    </div>
    <a href="#" (click)="openDialog('US Patriot Act Disclosure')">US Patriot Act Disclosure</a>
    <app-form-actions
      [step]="'first'"
      [isFormInvalid]="form.invalid"
      (continue)="continue()"
      (back)="goBack()"
    >
    </app-form-actions>
  </form>
</main>
